import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaLungs, FaBrain, FaCamera, FaFilter, FaCalendarAlt, FaSortAmountDown, FaFileAlt, FaChartLine, FaUserMd, FaEye, FaWalking, FaBaby } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { getUserHistory } from '../utils/api';

const TYPE_META = {
  xray: { label: 'Chest X-Ray', icon: <FaLungs className="text-indigo-400" /> },
  breast: { label: 'Breast Ultrasound', icon: <FaBrain className="text-purple-400" /> },
  skin: { label: 'Skin Lesion', icon: <FaCamera className="text-blue-400" /> },
  retina: { label: 'Retinal Fundus', icon: <FaEye className="text-teal-400" /> },
  foot: { label: 'Diabetic Foot', icon: <FaWalking className="text-amber-400" /> },
  fetal_plane: { label: 'Fetal Plane Check', icon: <FaBaby className="text-pink-400" /> },
};

const NORMAL_RISK_TIERS = new Set(['normal', 'benign', 'none']);

const PAGE_SIZE = 8;

const Analysis = () => {
  const { currentUser } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = currentUser?.token;
        const history = await getUserHistory(token);
        if (cancelled) return;
        const sorted = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
        setAnalyses(sorted);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load analysis history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  const stats = useMemo(() => {
    const total = analyses.length;
    const withPreds = analyses.filter((a) => a.predictions?.length);
    const avgConfidence = withPreds.length
      ? Math.round((withPreds.reduce((sum, a) => sum + (a.predictions[0].confidence || 0), 0) / withPreds.length) * 100)
      : 0;
    const abnormal = analyses.filter((a) => {
      const top = a.predictions?.[0];
      if (!top) return false;
      const risk = (top.risk || '').toLowerCase();
      const label = (top.label || '').toLowerCase();
      if (NORMAL_RISK_TIERS.has(risk)) return false;
      if (label === 'no finding') return false;
      return true;
    }).length;
    return { total, avgConfidence, abnormal };
  }, [analyses]);

  const totalPages = Math.max(1, Math.ceil(analyses.length / PAGE_SIZE));
  const pageItems = analyses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Analyses</h1>
              <p className="text-gray-400">View and manage your diagnostic analyses</p>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 flex items-center space-x-2 hover:bg-gray-800 transition-colors cursor-pointer">
                <FaFilter className="h-4 w-4" />
                <span>Filter</span>
              </button>
              <button className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 flex items-center space-x-2 hover:bg-gray-800 transition-colors cursor-pointer">
                <FaSortAmountDown className="h-4 w-4" />
                <span>Sort</span>
              </button>
              <button className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 flex items-center space-x-2 hover:bg-gray-800 transition-colors cursor-pointer">
                <FaCalendarAlt className="h-4 w-4" />
                <span>Date Range</span>
              </button>
            </div>
          </div>

          {/* Analysis Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-900 rounded-2xl shadow-md p-6 border border-gray-800 flex items-center space-x-4">
              <div className="p-3 bg-indigo-900/30 rounded-xl">
                <FaFileAlt className="h-6 w-6 text-indigo-400" />
              </div>
              <div>
                <div className="text-3xl font-bold text-white">{stats.total}</div>
                <div className="text-sm text-gray-400">Total Analyses</div>
              </div>
            </div>
            <div className="bg-gray-900 rounded-2xl shadow-md p-6 border border-gray-800 flex items-center space-x-4">
              <div className="p-3 bg-green-900/30 rounded-xl">
                <FaChartLine className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <div className="text-3xl font-bold text-white">{stats.total ? `${stats.avgConfidence}%` : '—'}</div>
                <div className="text-sm text-gray-400">Average AI Confidence</div>
              </div>
            </div>
            <div className="bg-gray-900 rounded-2xl shadow-md p-6 border border-gray-800 flex items-center space-x-4">
              <div className="p-3 bg-purple-900/30 rounded-xl">
                <FaUserMd className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <div className="text-3xl font-bold text-white">{stats.abnormal}</div>
                <div className="text-sm text-gray-400">Abnormal Findings</div>
              </div>
            </div>
          </div>

          {/* Analyses List */}
          <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden mb-10 border border-gray-800">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">Analysis History</h2>
              <p className="text-gray-400 text-sm mt-1">All your diagnostic analyses in one place, most recent first</p>
            </div>

            {loading && (
              <div className="p-10 text-center text-gray-400 text-sm">Loading your analyses…</div>
            )}

            {!loading && error && (
              <div className="p-10 text-center text-red-400 text-sm">{error}</div>
            )}

            {!loading && !error && analyses.length === 0 && (
              <div className="p-10 text-center text-gray-400 text-sm">
                No analyses yet — run one from "Start New Analysis" below.
              </div>
            )}

            {!loading && !error && pageItems.length > 0 && (
              <div className="divide-y divide-gray-800">
                {pageItems.map((analysis) => {
                  const meta = TYPE_META[analysis.type] || { label: analysis.type, icon: <FaFileAlt className="text-gray-400" /> };
                  const top = analysis.predictions?.[0];
                  const confidencePct = top ? Math.round((top.confidence || 0) * 100) : null;
                  const riskTier = (top?.risk || '').toLowerCase();
                  const isNormal = !top || NORMAL_RISK_TIERS.has(riskTier) || (top.label || '').toLowerCase() === 'no finding';
                  const dateLabel = analysis.date
                    ? new Date(analysis.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                    : 'Unknown date';

                  return (
                    <div key={analysis.id} className="p-6 hover:bg-gray-800/30 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-gray-800 rounded-xl">
                            {meta.icon}
                          </div>
                          <div>
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-medium text-white">{meta.label}</h3>
                              {analysis.review_status && (
                                <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  analysis.review_status === 'pending_review'
                                    ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                                    : 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                                }`}>
                                  {analysis.review_status === 'pending_review' ? 'Pending Review' : analysis.review_status}
                                </div>
                              )}
                            </div>

                            {analysis.patient_context && (
                              <div className="flex space-x-4 mt-1">
                                {analysis.patient_context.age != null && (
                                  <p className="text-sm text-gray-400">
                                    {analysis.patient_context.age} y{analysis.patient_context.sex ? `, ${analysis.patient_context.sex}` : ''}
                                  </p>
                                )}
                              </div>
                            )}

                            <p className="text-sm text-gray-400 mt-2">
                              Analyzed on {dateLabel}
                            </p>

                            <div className="mt-3">
                              <div className="flex items-center space-x-2">
                                <div className={`h-2 w-2 rounded-full ${isNormal ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <p className={`text-sm font-medium ${isNormal ? 'text-green-400' : 'text-red-400'}`}>
                                  {top?.label || 'No prediction recorded'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end">
                          <div className="mb-2">
                            <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              isNormal ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                              'bg-red-900/30 text-red-400 border border-red-500/30'
                            }`}>
                              {isNormal ? 'Normal' : 'Abnormal'}
                            </div>
                          </div>

                          {confidencePct != null && (
                            <div className="flex items-center mt-4">
                              <span className="text-xs text-gray-400 mr-2">AI confidence:</span>
                              <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    confidencePct > 70 ? 'bg-green-500' :
                                    confidencePct > 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${confidencePct}%` }}
                                ></div>
                              </div>
                              <span className="ml-2 text-xs text-gray-400">{confidencePct}%</span>
                            </div>
                          )}

                          <Link to={`/analysis/${analysis.id}`} className="mt-4 text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center">
                            View Report
                            <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && !error && analyses.length > 0 && (
              <div className="bg-black/30 px-6 py-4 flex justify-between items-center border-t border-gray-800">
                <p className="text-sm text-gray-400">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, analyses.length)} of {analyses.length} analyses
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-md bg-gray-800 text-gray-400 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={`px-3 py-1 rounded-md cursor-pointer ${
                        p === page
                          ? 'bg-purple-900/50 text-purple-300 border border-purple-700/30'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 rounded-md bg-gray-800 text-gray-400 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Start New Analysis */}
          <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden mb-8 border border-gray-800">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">Start New Analysis</h2>
              <p className="text-gray-400 text-sm mt-1">Select the type of analysis you want to perform</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                <Link
                  to="/analyze/xray"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-indigo-700 transition-all duration-300"
                >
                  <div className="p-4 bg-indigo-900/30 rounded-full mb-4 group-hover:bg-indigo-900/50 transition-colors duration-300">
                    <FaLungs className="h-8 w-8 text-indigo-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Chest X-Ray</h3>
                  <p className="text-sm text-gray-400 text-center">Detect 18 lung conditions from chest X-rays</p>
                </Link>

                <Link
                  to="/analyze/ct-scan"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-purple-700 transition-all duration-300"
                >
                  <div className="p-4 bg-purple-900/30 rounded-full mb-4 group-hover:bg-purple-900/50 transition-colors duration-300">
                    <FaBrain className="h-8 w-8 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Breast Ultrasound</h3>
                  <p className="text-sm text-gray-400 text-center">AI-powered breast lesion classification</p>
                </Link>

                <Link
                  to="/analyze/skin"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-blue-700 transition-all duration-300"
                >
                  <div className="p-4 bg-blue-900/30 rounded-full mb-4 group-hover:bg-blue-900/50 transition-colors duration-300">
                    <FaCamera className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Skin Lesion</h3>
                  <p className="text-sm text-gray-400 text-center">Cancer-subtype and general dermatology condition screening</p>
                </Link>

                <Link
                  to="/analyze/retina"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-teal-700 transition-all duration-300"
                >
                  <div className="p-4 bg-teal-900/30 rounded-full mb-4 group-hover:bg-teal-900/50 transition-colors duration-300">
                    <FaEye className="h-8 w-8 text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Retinal Fundus</h3>
                  <p className="text-sm text-gray-400 text-center">Screen for diabetic retinopathy, glaucoma, AMD & more</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-teal-900/40 text-teal-300 border border-teal-700/40 rounded-full">New</span>
                </Link>

                <Link
                  to="/analyze/foot"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-amber-700 transition-all duration-300"
                >
                  <div className="p-4 bg-amber-900/30 rounded-full mb-4 group-hover:bg-amber-900/50 transition-colors duration-300">
                    <FaWalking className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Diabetic Foot</h3>
                  <p className="text-sm text-gray-400 text-center">Classify foot ulcer type from clinical photos</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-amber-900/40 text-amber-300 border border-amber-700/40 rounded-full">New</span>
                </Link>

                <Link
                  to="/analyze/fetal-plane"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-pink-700 transition-all duration-300"
                >
                  <div className="p-4 bg-pink-900/30 rounded-full mb-4 group-hover:bg-pink-900/50 transition-colors duration-300">
                    <FaBaby className="h-8 w-8 text-pink-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Fetal Plane Check</h3>
                  <p className="text-sm text-gray-400 text-center">Scan-quality check for obstetric ultrasound frames</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-pink-900/40 text-pink-300 border border-pink-700/40 rounded-full">New</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Analysis;
